// Empire Apps Chassis · connectors/financial/upstox-groww-angel
// Broker family: Upstox · Groww · Angel One — read-only holdings.
// All three ship as mock-mode by default. Live OAuth code paths are stubbed.

import {
  type Connector,
  type ConnectorContext,
  type ConnectorPayload,
  type ConnectorResult,
  type ConnectorStatus,
  type HoldingPosition,
  brokerId,
  fail,
  isoTs,
  ok,
  paise,
} from './types.ts';
import { logConnectorConsent } from './dpdp-purposes.ts';
import { kvReadJson, kvWriteJson } from './storage-kv.ts';

const DEV = true;

interface BrokerDef {
  readonly id: string;
  readonly displayName: string;
  readonly oauthBase: string;
  readonly seed: ReadonlyArray<readonly [string, 'NSE' | 'BSE', number, number, number]>;
}

const BROKER_DEFS: Readonly<Record<string, BrokerDef>> = Object.freeze({
  upstox: {
    id: 'upstox',
    displayName: 'Upstox',
    oauthBase: 'https://api.upstox.com',
    seed: [
      ['TATAMOTORS', 'NSE', 75, 78400, 92500],
      ['MARUTI', 'NSE', 5, 1180000, 1265000],
      ['JSWSTEEL', 'NSE', 100, 81500, 87200],
      ['POWERGRID', 'NSE', 200, 23800, 32100],
    ],
  },
  groww: {
    id: 'groww',
    displayName: 'Groww',
    oauthBase: 'https://groww.in',
    seed: [
      ['ETERNAL', 'NSE', 50, 18500, 21200],
      ['NYKAA', 'NSE', 80, 15800, 18900],
      ['POLICYBZR', 'NSE', 30, 92500, 138000],
      ['PAYTM', 'NSE', 20, 75500, 92800],
    ],
  },
  'angel-one': {
    id: 'angel-one',
    displayName: 'Angel One',
    oauthBase: 'https://smartapi.angelbroking.com',
    seed: [
      ['ICICIBANK', 'NSE', 35, 102500, 124800],
      ['AXISBANK', 'NSE', 25, 95800, 112000],
      ['KOTAKBANK', 'NSE', 15, 168000, 195000],
      ['INDUSINDBK', 'NSE', 20, 142000, 132500],
    ],
  },
});

function statusKey(id: string): string {
  return `empire:financial:broker:${id}:status`;
}

interface PersistedStatus {
  readonly connected: boolean;
  readonly userId: string | null;
  readonly lastSync: string | null;
}

function readStatus(id: string): PersistedStatus {
  return kvReadJson<PersistedStatus>(statusKey(id), { connected: false, userId: null, lastSync: null });
}

function writeStatus(id: string, s: PersistedStatus): void {
  kvWriteJson(statusKey(id), s);
}

function mockFor(def: BrokerDef): readonly HoldingPosition[] {
  return def.seed.map(([sym, ex, qty, avg, ltp]) =>
    Object.freeze<HoldingPosition>({
      brokerId: brokerId(def.id),
      symbol: sym,
      exchange: ex,
      quantity: qty,
      avgCostPaise: paise(avg),
      ltpPaise: paise(ltp),
      asOf: isoTs(),
    }),
  );
}

function makeConnector(def: BrokerDef): Connector {
  return {
    id: def.id,
    displayName: def.displayName,
    category: 'broker',
    defaultMode: DEV ? 'mock' : 'live',

    async status(): Promise<ConnectorStatus> {
      const s = readStatus(def.id);
      return {
        id: this.id,
        displayName: this.displayName,
        category: this.category,
        mode: DEV ? 'mock' : 'live',
        connected: s.connected,
        lastSync: (s.lastSync as ReturnType<typeof isoTs> | null) ?? null,
        accountCount: s.connected ? 1 : 0,
        disabledReason: DEV ? null : `OAuth client pending for ${def.displayName}.`,
      };
    },

    async connect(ctx: ConnectorContext, opts?: Record<string, string>): Promise<ConnectorResult<{ readonly connectedAt: ReturnType<typeof isoTs> }>> {
      const userId = opts?.userId ?? `${def.id.toUpperCase()}_USR`;
      logConnectorConsent(ctx.dpdpLog, 'connector-broker-read', `${def.id}/holdings`);
      const connectedAt = isoTs();
      writeStatus(def.id, { connected: true, userId, lastSync: connectedAt });
      return ok({ connectedAt });
    },

    async import(): Promise<ConnectorResult<ConnectorPayload>> {
      const s = readStatus(def.id);
      if (!s.connected) return fail(`Not connected to ${def.displayName}.`, true);
      const holdings = mockFor(def);
      writeStatus(def.id, { ...s, lastSync: isoTs() });
      return ok({ holdings });
    },

    async disconnect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly disconnectedAt: ReturnType<typeof isoTs> }>> {
      writeStatus(def.id, { connected: false, userId: null, lastSync: null });
      ctx.dpdpLog('connector-consent-withdrawn', { purpose: 'connector-broker-read', broker: def.id });
      return ok({ disconnectedAt: isoTs() });
    },

    mockData(): ConnectorPayload {
      return { holdings: mockFor(def) };
    },
  };
}

export const upstoxConnect: Connector = makeConnector(BROKER_DEFS.upstox!);
export const growwConnect: Connector = makeConnector(BROKER_DEFS.groww!);
export const angelOneConnect: Connector = makeConnector(BROKER_DEFS['angel-one']!);
